import {
  ComponentRef,
  Directive,
  ElementRef,
  HostListener,
  OnDestroy,
  inject,
  input,
} from '@angular/core';
import {
  ConnectedPosition,
  FlexibleConnectedPositionStrategy,
  Overlay,
  OverlayRef,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { TooltipComponent } from './tooltip.component';

type TooltipPosition = 'top' | 'right' | 'bottom' | 'left' | 'self';

@Directive({
  selector: '[appTooltip]',
  standalone: true,
})
export class TooltipDirective implements OnDestroy {
  readonly appTooltip = input<string>('');
  readonly appTooltipPosition = input<TooltipPosition>('right');
  readonly appTooltipDisabled = input(false);
  readonly appTooltipOffset = input(5);
  readonly appTooltipShowDelay = input(10);

  private readonly hostElementRef = inject(ElementRef<HTMLElement>);
  private readonly overlay = inject(Overlay);

  private overlayRef: OverlayRef | null = null;
  private tooltipRef: ComponentRef<TooltipComponent> | null = null;
  private showTimeoutId: ReturnType<typeof setTimeout> | null = null;

  @HostListener('mouseenter')
  @HostListener('focusin')
  protected handleShowIntent(): void {
    this.scheduleShow();
  }

  @HostListener('mouseleave')
  @HostListener('focusout')
  protected handleHideIntent(): void {
    this.hide();
  }

  @HostListener('keydown.escape')
  protected handleEscape(): void {
    this.hide();
  }

  ngOnDestroy(): void {
    this.clearShowTimeout();
    this.overlayRef?.dispose();
    this.overlayRef = null;
    this.tooltipRef = null;
  }

  private scheduleShow(): void {
    if (this.appTooltipDisabled()) {
      return;
    }

    const text = this.appTooltip().trim();
    if (!text) {
      return;
    }

    this.clearShowTimeout();

    const delay = this.appTooltipShowDelay();
    if (delay > 0) {
      this.showTimeoutId = setTimeout(() => this.show(text), delay);
      return;
    }

    this.show(text);
  }

  private show(text: string): void {
    if (this.appTooltipDisabled()) {
      return;
    }

    const overlayRef = this.ensureOverlayRef();

    if (!overlayRef.hasAttached()) {
      this.tooltipRef = overlayRef.attach(new ComponentPortal(TooltipComponent));
    }

    if (this.tooltipRef) {
      this.tooltipRef.instance.text = text;
      this.tooltipRef.changeDetectorRef.markForCheck();
    }

    this.updatePositionStrategy();
  }

  private hide(): void {
    this.clearShowTimeout();
    if (!this.overlayRef?.hasAttached()) {
      return;
    }

    this.overlayRef.detach();
    this.tooltipRef = null;
  }

  private ensureOverlayRef(): OverlayRef {
    if (this.overlayRef) {
      return this.overlayRef;
    }

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.hostElementRef)
      .withFlexibleDimensions(false)
      .withPush(false)
      .withLockedPosition(true)
      .withPositions(this.getPositions());

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      panelClass: 'app-tooltip-overlay-pane',
    });

    return this.overlayRef;
  }

  private updatePositionStrategy(): void {
    if (!this.overlayRef) {
      return;
    }

    const strategy = this.overlayRef.getConfig().positionStrategy;
    if (!(strategy instanceof FlexibleConnectedPositionStrategy)) {
      return;
    }

    strategy.withPositions(this.getPositions());

    if (!this.overlayRef.hasAttached()) {
      return;
    }

    this.overlayRef.updatePosition();
  }

  private getPositions(): ConnectedPosition[] {
    const offset = this.appTooltipOffset();

    const right: ConnectedPosition = {
      originX: 'end',
      originY: 'center',
      overlayX: 'start',
      overlayY: 'center',
      offsetX: offset,
    };

    const left: ConnectedPosition = {
      originX: 'start',
      originY: 'center',
      overlayX: 'end',
      overlayY: 'center',
      offsetX: -offset,
    };

    const top: ConnectedPosition = {
      originX: 'center',
      originY: 'top',
      overlayX: 'center',
      overlayY: 'bottom',
      offsetY: -offset,
    };

    const bottom: ConnectedPosition = {
      originX: 'center',
      originY: 'bottom',
      overlayX: 'center',
      overlayY: 'top',
      offsetY: offset,
    };

    const self: ConnectedPosition = {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'top',
    };

    switch (this.appTooltipPosition()) {
      case 'self':
        return [self];
      case 'left':
        return [left, right, top, bottom];
      case 'top':
        return [top, bottom, right, left];
      case 'bottom':
        return [bottom, top, right, left];
      case 'right':
      default:
        return [right, left, top, bottom];
    }
  }

  private clearShowTimeout(): void {
    if (!this.showTimeoutId) {
      return;
    }

    clearTimeout(this.showTimeoutId);
    this.showTimeoutId = null;
  }
}
